import { Container } from '@acies/core';
import Icon from './icon';
import { ICONS } from './icons';
import theme from './theme';

/**
 * A round frame with a soft stroke, which an icon is centred in.
 *
 * Fixed at 40 by 40 rather than hugging what it holds, so a row of them lines up whatever each icon's own size.
 *
 * Which icon it shows is {@link IconRing.name}, proxied to the icon the way `IconTitleSection` proxies its icon to its header, so the ring holds no name of its own. `alert-circle` until it is told otherwise.
 */
export default class IconRing extends Container {
	public set name(value: keyof typeof ICONS | null) {
		this.icon.name = value;
	}

	public get name(): keyof typeof ICONS | null {
		return this.icon.name;
	}

	/**
	 * Which of the ring's looks it has: soft for a ring that says nothing in particular, error and warning for one that does.
	 *
	 * The ring and its icon change together — the stroke and the fill of the one, the variant of the other — so a caller sets one thing and cannot leave the three disagreeing. The fill is the lighter tint of the stroke's colour, and white for the ring that says nothing in particular. Committed rather than applied in
	 * the setter, the way `Icon` commits its own variant, so setting it twice costs one pass.
	 *
	 * @param value Default 'default'
	 */
	public set variant(value: 'default' | 'error' | 'warning') {
		if (this.#variant === value) {
			return;
		}
		assertIsValidVariant(value);
		this.#variant = value;
		this.#variantChanged = true;
		this.invalidateProperties();
	}

	public get variant(): 'default' | 'error' | 'warning' {
		return this.#variant;
	}

	#icon!: Icon;
	#variant: 'default' | 'error' | 'warning';
	#variantChanged: boolean;

	public constructor() {
		super();
		this.width = 40;
		this.height = 40;
		// half the side, so the square is drawn as a circle
		this.cornerRadius = 20;
		this.stroke = theme.strokes.soft;
		this.fill = theme.colors.fill.white;
		this.alignment = 'center';
		this.#variant = 'default';
		this.#variantChanged = false;
	}

	public get icon(): Icon {
		if (!this.#icon) {
			this.#icon = new Icon();
			this.#icon.name = 'alert-circle';
		}
		return this.#icon;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponent(this.icon);
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#variantChanged) {
			this.#commitVariant();
		}
	}

	#commitVariant(): void {
		this.#variantChanged = false;
		if (this.variant === 'default') {
			this.stroke = theme.strokes.soft;
			this.fill = theme.colors.fill.white;
			this.icon.variant = 'default';
		} else if (this.variant === 'error') {
			this.stroke = theme.strokes.error;
			this.fill = theme.colors.error.lighter;
			this.icon.variant = 'error';
		} else if (this.variant === 'warning') {
			this.stroke = theme.strokes.warning;
			this.fill = theme.colors.warning.lighter;
			this.icon.variant = 'warning';
		}
	}
}
customElements.define('ac-icon-ring', IconRing);

function assertIsValidVariant(value: unknown): asserts value is 'default' | 'error' | 'warning' {
	if (value === 'default' || value === 'error' || value === 'warning') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be either "default", "error" or "warning"`);
}
